import * as React from "react";

interface IGlobalReactContext {
    context: React.Context<unknown>;
}

type GlobalObject = typeof globalThis & Record<symbol, IGlobalReactContext>;
const isBrowser = typeof window !== "undefined";
const globalObject: GlobalObject = isBrowser ? window : global;
const SYMBOL_NAMESPACE = "lodestar-global-context:";

/**
 * Wrapper around @see React.createContext that implements context registration
 * in the globalThis object to avoid duplicate contexts. Duplicate contexts can
 * occur when there are multiple instances of lodestar-core running at the same
 * time.
 *
 * Example:
 *
 *  - package A exposes a custom MenuItem: "CustomMenuItem"
 *  - package B uses the Menu and includes CustomMenuItem in its list of items.
 *  - depending on how the dependencies are resolved for A and B, there could
 *    be 2 versions of lodestar-core imported.
 *
 * This can cause useContext() calls to return different instances of the Menu
 * context objects which means they won't work together. This utility will allow
 * the first lodestar-core instance to publish its context on the `window` and let
 * any future lodestar-core instances to use the same instance.
 *
 * Note: React does not share contexts across React versions.  For example, React v16.1 and v16.2 will have their own
 *  context space if both versions are running in the same browser.  This function will bake the React version
 *  into the global symbol name to prevent the wrong react version context being returned to the caller.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol}
 *
 * @param defaultValue - @see React.createContext
 * @param contextName - name of the context
 * @returns @see React.createContext
 */
export const createContext = <T>(defaultValue: T, contextName: string) => {
    // Symbol guaranteed to be unique for the entire runtime
    const sym = Symbol.for(`${SYMBOL_NAMESPACE}${React.version}:${contextName}`);

    const globalSymbols = Object.getOwnPropertySymbols(globalObject);
    if (!globalSymbols.includes(sym)) {
        const context = React.createContext(defaultValue) as React.Context<unknown>;
        context.displayName = contextName;
        globalObject[sym] = {
            context,
        };
    }

    return globalObject[sym]?.context as React.Context<T>;
};
